import React, { useEffect, useState, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  Description as ReportsIcon,
  Lightbulb as InsightsIcon,
  CheckCircle as ApprovalsIcon,
  Folder as ProjectsIcon,
  People as ClientsIcon,
  Group as MembersIcon,
  Receipt as InvoicesIcon,
  LocalOffer as TagsIcon,
  Rocket as GoalsIcon,
  Extension as IntegrationsIcon,
  CreditCard as SubscriptionIcon,
  Business as OrganizationIcon,
  Settings as SettingsIcon,
  ChevronLeft,
  Assignment as RolesIcon,
  Psychology as SkillsIcon,
  AccountTree as BusinessUnitIcon,
  Task as TasksIcon,
  AccessTime as TimesheetIcon,
  Public as CountriesIcon,
  Factory as IndustriesIcon,
  Flag as MilestonesIcon,
  Description as DeliverablesIcon,
  Groups as ResourcesIcon,
  ChangeCircle as ChangesIcon,
  ContactMail as PocIcon,
  ContactPhone as ClientPocIcon,
  Category as SkillCategoryIcon,
  WorkspacePremium as RoleCategoryIcon,
  Logout as LogoutIcon,
  Work as WorkIcon,
  HelpOutline as HelpOutlineIcon,
  Person as PersonIcon,
  Apps as AppsIcon,
  Notifications as NotificationsIcon,
  History as HistoryIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Factory,
  School,
} from "@mui/icons-material";
import {
  SidebarContainer,
  PrimarySidebar,
  SecondarySidebar,
  MenuSection,
  MenuSubSection,
  MenuTitle,
  MenuItem,
  MenuBadge,
  CollapseButton,
  PrimaryMenuItem,
  ScrollableContent,
  ProfileCard,
  ProfileCardContent,
  ProfileName,
  ProfileRole,
  ProfileCollapseSection,
  ProfileSectionName,
} from "./Sidebar.styled";
import {
  IconButton,
  Avatar,
  Typography,
  Divider,
  alpha,
  Box,
} from "@mui/material";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import { useAuth } from "../../hooks/useAuth";
import { useRbac } from "../../hooks/RbacContext";
import { ROUTES } from "../../constants";

const Sidebar = ({ onCollapse }) => {
  const theme = useTheme();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const profileCardRef = useRef(null);
  const profileButtonRef = useRef(null);

  const toggleExpansion = (itemIndex) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemIndex]: !prev[itemIndex], // Toggle the expanded state
    }));
  };

  useEffect(() => {
    let user = localStorage.getItem("userDetails");
    if (user) {
      setUserDetails(JSON.parse(user));
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileCardRef.current &&
        !profileCardRef.current.contains(event.target) &&
        !profileButtonRef.current.contains(event.target)
      ) {
        setShowProfileCard(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const { checkAccess, permissions } = useRbac();

  const menuItems = [
    {
      title: "ANALYZE",
      items: [
        {
          name: "Analytics Dashboard",
          icon: <AnalyticsIcon />,
          path: ROUTES.ANALYTICS_DASHBOARD,
          module: "analytics",
          action: "view",
        },
        {
          name: "Reports",
          icon: "/icons/Reports.png",
          path: ROUTES.REPORTS,
          module: "reports",
          action: "view",
        },
      ],
    },
    {
      title: "MANAGE",
      items: [
        {
          name: "Clients",
          icon: "/icons/Clients.png",
          path: ROUTES.CLIENT,
          module: "client",
          action: "view",
        },
        {
          name: "Projects",
          icon: "/icons/Projects.png",
          path: ROUTES.PROJECT,
          module: "projects",
          action: "view",
        },
        {
          name: "Members",
          icon: "/icons/Members.png",
          path: ROUTES.RESOURCE,
          module: "resource",
          action: "view",
        },
        {
          name: "Timesheet",
          icon: "/icons/schedule.png",
          path: ROUTES.TIMESHEET,
          module: "Timesheet",
          action: "view",
        },
      ],
    },
    {
      title: "ADMIN",
      items: [
        {
          name: "Settings",
          icon: "/icons/Settings.png",
          module: "Timesheet",
          action: "view",
          subItem: [
            {
              name: "Role Categories",
              icon: <RoleCategoryIcon />,
              path: ROUTES.ROLES_CATEGORIES,
              module: "roleCategory",
              action: "view",
            },
            {
              name: "Roles",
              icon: <RolesIcon />,
              path: ROUTES.ROLES,
              module: "role",
              action: "view",
            },
            {
              name: "Skill Categories",
              icon: <SkillCategoryIcon />,
              path: ROUTES.ADMINISTRATION.SKILL_CATEGORY,
              module: "skillCategory",
              action: "view",
            },
            {
              name: "Skills",
              icon: <SkillsIcon />,
              path: ROUTES.ADMINISTRATION.SKILL,
              module: "skill",
              action: "view",
            },
            {
              name: "Resource Skills",
              icon: <School />,
              path: ROUTES.RESOURCE_SKILLS,
              module: "resourceSkillMapping",
              action: "view",
            },
            {
              name: "Countries",
              icon: <CountriesIcon />,
              path: ROUTES.COUNTRY,
              module: "country",
              action: "view",
            },
            {
              name: "Cities",
              icon: <LocationCityIcon />,
              path: ROUTES.ADMINISTRATION.CITY,
              module: "city",
              action: "view",
            },
            {
              name: "Business Units",
              icon: <BusinessUnitIcon />,
              path: ROUTES.BUSINESS_UNIT,
              module: "businessUnit",
              action: "view",
            },
            {
              name: "Client Industries",
              icon: <Factory />,
              path: ROUTES.CLIENT_INDUSTRIES,
              module: "clientIndustry",
              action: "view",
            },
            {
              name: "Tags & Activities",
              icon: <SkillsIcon />,
              path: ROUTES.TAGS,
              module: "tag",
              action: "view",
            },
            {
              name: "Activity Logs",
              icon: <HistoryIcon />,
              path: ROUTES.ACTIVITY_LOGS,
              module: "logs",
              action: "view",
            },
          ],
        },
      ],
    },
  ];

  const filteredMenuItems = menuItems
    .map((section) => ({
      ...section,
      items: section.items
        .map((item) => ({
          ...item,
          subItem: item.subItem?.filter(
            (subitem) =>
              !subitem.module || checkAccess(subitem.module, subitem.action)
          ),
        }))
        .filter(
          (item) =>
            !item.module ||
            checkAccess(item.module, item.action) ||
            (item.subItem && item.subItem.length > 0)
        ),
    }))
    .filter((section) => section.items.length > 0);

  const handleNavigation = (path) => {
    navigate(path);
    if (path === ROUTES.TIMESHEET) {
      const newCollapsedState = true;
      setIsCollapsed(newCollapsedState);
      onCollapse?.(newCollapsedState);
    }
  };

  const handleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onCollapse?.(newCollapsedState);
  };

  const handleLogout = async () => {
    console.log("Logging out...");

    await logout();
    console.log("Redirecting to /login");
    window.location.replace("/login");
  };

  const handleProfileClick = () => {
    setShowProfileCard(!showProfileCard);
  };

  return (
    <SidebarContainer>
      <PrimarySidebar theme={theme}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <PrimaryMenuItem
            onClick={() => {
              navigate("/timesheet");
            }}
          >
            <TimesheetIcon />
          </PrimaryMenuItem>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CollapseButton onClick={handleCollapse} collapsed={isCollapsed}>
            <ChevronLeft />
          </CollapseButton>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <PrimaryMenuItem ref={profileButtonRef} onClick={handleProfileClick}>
            <PersonIcon />
          </PrimaryMenuItem>

          <PrimaryMenuItem onClick={handleLogout}>
            <LogoutIcon />
          </PrimaryMenuItem>
        </Box>
      </PrimarySidebar>

      <ProfileCard
        ref={profileCardRef}
        className={showProfileCard ? "show" : ""}
      >
        <ProfileCardContent>
          <ProfileName>
            {userDetails?.first_name} {userDetails?.last_name}
          </ProfileName>
          <ProfileRole>{permissions?.globalRole?.role_name || "User"}</ProfileRole>
        </ProfileCardContent>
      </ProfileCard>

      <SecondarySidebar theme={theme} collapsed={isCollapsed}>
        <ProfileCollapseSection>
          <ProfileName>Welcome Back</ProfileName>
          <ProfileSectionName>
            {userDetails?.first_name} {userDetails?.last_name}!
          </ProfileSectionName>
        </ProfileCollapseSection>

        <ScrollableContent>
          {filteredMenuItems.map((section, index) => (
            <MenuSection key={index}>
              <MenuTitle
                style={{
                  display:
                    section?.items &&
                    section?.items.length > 0 &&
                    section?.items[0].name != "Settings"
                      ? "flex"
                      : "none",
                }}
                theme={theme}
              >
                {section.title}
              </MenuTitle>
              {section.items.map((item, itemIndex) => (
                <>
                  <MenuItem
                    key={itemIndex}
                    theme={theme}
                    active={location.pathname === item.path}
                    onClick={() =>
                      item?.subItem
                        ? toggleExpansion(`${index}-${itemIndex}`)
                        : handleNavigation(item.path)
                    }
                    style={{
                      display:
                        !item?.subItem ||
                        (item?.subItem && item?.subItem.length > 0)
                          ? "flex"
                          : "none",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }} // Flexbox for alignment
                  >
                    {!item?.subItem ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        {typeof item.icon === "string" ? (
                          <img src={item.icon} alt="Icon" width="15px" />
                        ) : (
                          item.icon
                        )}
                        <span>{item.name}</span>
                      </div>
                    ) : (
                      item?.subItem.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          {typeof item.icon === "string" ? (
                            <img src={item.icon} alt="Icon" width="15px" />
                          ) : (
                            item.icon
                          )}
                          <span>{item.name}</span>
                        </div>
                      )
                    )}
                    <span>
                      {item?.subItem && item?.subItem.length > 0 ? (
                        expandedItems[`${index}-${itemIndex}`] ? (
                          <KeyboardArrowUpIcon sx={{ mt: "5px" }} />
                        ) : (
                          <KeyboardArrowDownIcon sx={{ mt: "5px" }} />
                        )
                      ) : null}
                    </span>
                    {item.badge && <MenuBadge>{item.badge}</MenuBadge>}
                  </MenuItem>
                  {item?.subItem && expandedItems[`${index}-${itemIndex}`] && (
                    <MenuSubSection key={itemIndex}>
                      {item?.subItem?.map((subitem, subitemIndex) => (
                        <MenuItem
                          key={subitemIndex}
                          theme={theme}
                          active={location.pathname === subitem.path}
                          onClick={() => handleNavigation(subitem.path)}
                        >
                          {subitem.icon}
                          <span>{subitem.name}</span>
                          {subitem.badge && (
                            <MenuBadge>{subitem.badge}</MenuBadge>
                          )}
                        </MenuItem>
                      ))}{" "}
                    </MenuSubSection>
                  )}
                </>
              ))}
            </MenuSection>
          ))}
        </ScrollableContent>
      </SecondarySidebar>
    </SidebarContainer>
  );
};

export default Sidebar;
